import React, { useEffect, useState } from "react";
import performRequest from "../../../api/request";
import { Toast } from "../../../CustomComponent/toast";
import { CustomErrorResponse } from "../../../CustomComponent/custom_error_response";
import CustomViewButton from "../../../CustomComponent/custom_view_button";
import { useNavigate } from "react-router-dom";
import { FcPaid } from "react-icons/fc";
import ButtonWithSVG from "../../../Components/layouts/button_with_svg";

const ShopifyProducts = () => {
  const [products, setProducts] = useState([]);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchProducts = (pageInfo = null, query = "") => {
    let url = `/shpkOrder/fetch-inventory?limit=50`;
    if (pageInfo) url += `&page_info=${pageInfo}`;
    if (query) url += `&title=${encodeURIComponent(query)}`;

    setLoading(true);
    performRequest("GET", url)
      .then((res) => {
        if (res.status === 200) {
          setProducts(res.data.products || []);
          setNextPage(res.data.nextPage || null);
          setPrevPage(res.data.prevPage || null);
        } else {
          Toast("error", "Error while fetching Shopify products!");
        }
      })
      .catch((err) => {
        CustomErrorResponse(err);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProducts(null, searchTerm.trim());
  };

  const clearSearch = () => {
    setSearchTerm("");
    fetchProducts();
  };

  const goToView = (id) => {
    navigate(`/shopify-product/view/${id}`);
  };

  return (
    <div className="container-xl py-4">
      <div className="text-center">
        <h2 className="fw-bold mb-0 text-center fs-1 text-uppercase">
          <FcPaid style={{ fontSize: "40px", marginBottom: "10px" }}></FcPaid>{" "}
          Kryzen Store
        </h2>
        <div className="col-auto ms-auto d-print-none text-end">
          <div className="d-flex d-none d-sm-block">
            <ButtonWithSVG
              href={"/add-product"}
              buttonText="Add New Product"
              svg={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M12 5l0 14" />
                  <path d="M5 12l14 0" />
                </svg>
              }
            />
          </div>
        </div>
      </div>
      <hr></hr>

      {/* Search */}
      <form onSubmit={handleSearch} className="mb-4">
        <div className="input-group input-group-lg">
          <input
            type="text"
            placeholder="Search products by title…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-control"
          />
          <button type="submit" className="btn btn-primary">
            Search
          </button>

          <button
            type="button"
            className="btn btn-outline-black"
            onClick={clearSearch}
            title="Clear"
          >
            Reset
          </button>
        </div>
      </form>

      {/* Card + Table */}
      <div className="card shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover table-bordered align-middle fs-5">
            <thead className="table-dark">
              <tr>
                <th style={{ width: 150 }} className="p-3">
                  Product ID
                </th>
                <th className="p-3">Title</th>
                <th style={{ width: 150 }} className="text-center p-3">
                  Variants
                </th>
                <th style={{ width: 150 }} className="text-end p-3">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {!loading && products?.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center py-5 text-muted fs-5">
                    No products found.
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td colSpan={4} className="text-center py-5">
                    <div className="spinner-border" role="status" />
                    <div className="small text-muted mt-2">Loading…</div>
                  </td>
                </tr>
              )}

              {!loading &&
                products?.map((p) => (
                  <tr key={p.id}>
                    <td className="fw-semibold text-nowrap p-3 fs-4">{p.id}</td>
                    <td className="p-3">
                      <div className="fw-bold fs-4">{p.title}</div>
                      <div className="text-muted small text-truncate">
                        {p.product_type && (
                          <span className="badge text-bg-light me-1 ">
                            {p.product_type}
                          </span>
                        )}
                        {p.status && (
                          <span
                            className={`badge me-1 ${
                              p.status === "active"
                                ? "text-bg-success"
                                : "text-bg-secondary"
                            }`}
                          >
                            {p.status}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="text-center p-3">
                      <span className="badge text-bg-primary fs-6 px-3 py-2">
                        {p?.variants?.length || 0}
                      </span>
                    </td>
                    <td className="text-end p-3">
                      <CustomViewButton onClick={() => goToView(p.id)} />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="card-footer d-flex justify-content-between align-items-center py-3">
          <button
            className="btn btn-primary mx-1 mt-2"
            disabled={!prevPage || loading}
            onClick={() => fetchProducts(prevPage, searchTerm)}
          >
            ‹ Previous
          </button>
          <div className="small text-muted">
            <div className="text-center">
              <span className="text-muted small">
                {loading ? "Loading…" : `${products?.length || 0} items`}
              </span>
            </div>
            {nextPage || prevPage ? "Use arrows to navigate pages" : ""}
          </div>
          <button
            className="btn btn-primary mx-1 mt-2"
            disabled={!nextPage || loading}
            onClick={() => fetchProducts(nextPage, searchTerm)}
          >
            Next ›
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShopifyProducts;
